import { useRef, useState } from 'react'
import PropTypes from 'prop-types'
import styled, { css } from 'styled-components'

import colors from 'components/utils/colors'
import IconLink from 'components/Shared/IconLink/Index'

const HEIGHT = 32
const WIDTH = 122
const MAX_DIGITS = 10

const StyledWrapper = styled.div`
  width: ${props => props.width || WIDTH}px;
  position: relative;
  display: flex;
  align-items: center;
`

const styleInputAsError = css`
  && {
    border-color: ${colors.alert};
  }
`

const StyledInput = styled.input`
  display: flex;
  max-width: ${props => props.width || WIDTH}px;
  box-sizing: border-box;
  flex: 1;
  font-weight: 400;
  font-size: 14px;
  line-height: 16px;
  height: ${HEIGHT}px;
  color: ${colors.secondary20};
  border-radius: 3px;
  border: 1px solid ${colors.secondary70};
  padding: 0 0 0 7px;
  outline: none;
  &::placeholder {
    color: ${colors.secondary70};
  }
  &:focus, &:hover {
    border: 1px solid ${colors.primary40};
  }

  /* Hide the original arrow buttons */
  /* Chrome, Safari, Edge, Opera */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  /* Firefox */
  &[type=number] {
    -moz-appearance: textfield;
  }

  ${props => props.isError && styleInputAsError}
`

const StyledClearButton = styled(IconLink)`
  position: absolute;
  right: 28px;
  padding: 9px 1px;
`

const IconsWrapper = styled.div`
  position: absolute;
  right: 1px;
  top: 3px;
`

const UpDownIconsWrapper = styled.div`
  position: absolute;
  right: 0;
  display: flex;
  flex-direction: column;
`

const IconUp = styled(IconLink)`
  padding: 0 4px 1px 10px;
  transform: rotate(180deg);
`

const IconDown = styled(IconLink)`
  padding: 1px 10px 0 4px;
`

const toInt = (val) => {
  const isEmpty = Boolean(val === null || val === '' || val === undefined)
  if (isEmpty) {
    return null
  }
  let intVal = parseInt(val, 10)
  if (isNaN(intVal)) {
    intVal = null
  } else {
    intVal = Math.abs(intVal)
  }
  return intVal
}

// initial value may be empty (null, undefined or empty string), string with integer or number
// onChange returns value of null or number
const IntegerField = ({
  value, onChange, minNumber, disabled, dataTest, placeholder,
  className, defaultValue, isError, width,
}) => {
  const inputFieldRef = useRef(null)
  const [hasHover, setHasHover] = useState(false)
  const [hasFocus, setHasFocus] = useState(false)

  const defaultValue_ = (defaultValue || defaultValue === 0) ? defaultValue : null
  const intValueOrNull = toInt(value)
  const isEmpty = Boolean(intValueOrNull === null)
  const minNumber_ = minNumber || 0
  const isError_ = Boolean(isError || (!isEmpty && intValueOrNull < minNumber_))

  const setInputFocus = () => {
    inputFieldRef.current.focus()
  }

  const onMouseEnter = () => {
    setHasHover(true)
  }

  const onMouseLeave = () => {
    setHasHover(false)
  }

  const onChange_ = (val) => {
    // ignore long input
    if (val && (val + '').length > MAX_DIGITS) {
      return
    }

    onChange(val)
  }

  const onInputChange = (e) => {
    onChange_(toInt(e.target.value))
  }

  const onKeyDown = (e) => {
    if (e.key === 'ArrowUp') {
      increment()
      e.preventDefault()
    } else if (e.key === 'ArrowDown') {
      decrement()
      e.preventDefault()
    }
  }

  const onInputFocus = () => {
    setHasFocus(true)
  }

  // check that value >= minNumber only on blur
  // if we check it immediately on input, user will not be able to enter '10' for minValue='9'
  const onBlur = (e) => {
    setHasFocus(false)
    const currentTarget = e.currentTarget

    // Check the newly focused element in the next tick of the event loop
    setTimeout(() => {
      // Check if the new activeElement is a child of the original container
      const el = document.activeElement
      if (!currentTarget.contains(el)) {
        const newValue = toInt(e.target.value)
        if (newValue !== null && newValue < minNumber_) {
          onChange_(minNumber_)
        }
      }
    }, 0)
  }

  const onClear = () => {
    onChange_(defaultValue_)
    setInputFocus()
  }

  const increment = () => {
    onChange_((isEmpty || intValueOrNull < minNumber_) ? minNumber_ : intValueOrNull + 1)
  }

  const decrement = () => {
    onChange_((isEmpty || intValueOrNull <= minNumber_) ? minNumber_ : intValueOrNull - 1)
  }

  const onIncrement = () => {
    increment()
    setInputFocus()
  }

  const onDecrement = () => {
    decrement()
    setInputFocus()
  }

  // for empty fields show clear icon only when component has focus or mouse hover
  const showClearIcon = Boolean(
    !disabled && intValueOrNull !== defaultValue_ &&
    (!isEmpty || (isEmpty && (hasHover || hasFocus)))
  )
  const isDownDisabled = Boolean(intValueOrNull === minNumber_)

  // we set '' in input because React considers components with null
  // or undefined values to be uncontrolled
  return (
    <StyledWrapper
      className={className}
      onBlur={onBlur}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      width={width}
    >
      <StyledInput
        ref={inputFieldRef}
        type='text'
        value={intValueOrNull ?? ''}
        onChange={onInputChange}
        onKeyDown={onKeyDown}
        onFocus={onInputFocus}
        disabled={disabled}
        data-test={dataTest}
        min={minNumber_}
        placeholder={placeholder}
        isError={isError_}
        width={width}
      />
      <IconsWrapper tabIndex={-1}>
        {showClearIcon && (
          <StyledClearButton
            iconName='icon-clear-search'
            onClick={onClear}
          />
        )}
        {!disabled && (
          <UpDownIconsWrapper>
            <IconUp iconName='icon-arrow-down-12' onClick={onIncrement} />
            <IconDown iconName='icon-arrow-down-12' onClick={onDecrement} disabled={isDownDisabled} />
          </UpDownIconsWrapper>
        )}
      </IconsWrapper>
    </StyledWrapper>
  )
}

IntegerField.propTypes = {
  value: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]),
  onChange: PropTypes.func.isRequired,
  minNumber: PropTypes.number,
  disabled: PropTypes.bool,
  dataTest: PropTypes.string,
  placeholder: PropTypes.string,
  defaultValue: PropTypes.number,
  isError: PropTypes.bool,
  width: PropTypes.number,
}

export default IntegerField
